import { RefObject, useEffect, useState } from 'react';

/**
 * `top` de los paneles laterales sticky: la cabecera es fija (~70 px, ver `.full-layout__container__telemed`) y el
 * panel se queda 16 px por debajo. Con menos, al bajar la página el panel quedaría tapado por la cabecera.
 */
export const STICKY_PANEL_TOP = 86;

/**
 * Hueco bajo el contenedor que se considera solo márgenes (padding de la página, margen inferior de la tarjeta).
 * Por encima de esto, lo que hay debajo es contenido real y la página tiene scroll igualmente.
 */
const MAX_TRAILING_MARGIN = 80;

interface Options {
  /** El `top` del panel sticky: al bajar la página se queda a esta distancia del borde superior. */
  stickyTop?: number;
  /** Margen que se deja con el borde inferior de la ventana. */
  bottomGap?: number;
  minHeight?: number;
}

/**
 * Alto para un panel lateral sticky (árbol de grupos, lista de tipos de regla) que ocupe toda la pantalla
 * disponible sin alargar nunca la página.
 *
 * - Con la página arriba del todo, llega justo al borde inferior de la ventana.
 * - Al bajar y quedarse fijo arriba, crece hasta ocupar la pantalla entera.
 * - Nunca pasa del final del contenido que tiene al lado (sus hermanos en el contenedor). Esa es la clave: si el
 *   panel pudiera crecer más allá, alargaría la página, se podría bajar más, crecería más… sin fin. El contenido de
 *   al lado no depende del alto del panel, así que el tope es estable.
 *
 * Se mide, no se calcula con un `calc(100vh - Xpx)`: lo que hay encima (cabecera, pestañas, tarjetas) cambia de
 * alto según la pantalla. Las posiciones se toman del contenedor padre y de los hermanos, no del panel: uno sticky
 * ya fijado arriba no está en su sitio de la página.
 */
export const useAvailableHeight = (
  ref: RefObject<HTMLElement>,
  { stickyTop = STICKY_PANEL_TOP, bottomGap = 16, minHeight = 200 }: Options = {},
): number | undefined => {
  const [height, setHeight] = useState<number>();

  useEffect(() => {
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const el = ref.current;
        const parent = el?.parentElement;
        if (!el || !parent) return;

        const parentRect = parent.getBoundingClientRect(); // en la ventana
        const parentTopInPage = parentRect.top + window.scrollY;

        // Lo que la página deja por debajo del contenedor. Si son solo márgenes, el panel los respeta en vez de los
        // 16 px fijos: si no, en una página que cabe entera quedaba un poco de scroll. Es estable: crece igual el
        // final del contenedor que el del documento, así que no depende del alto del panel.
        const trailing = document.documentElement.scrollHeight - (parentRect.bottom + window.scrollY);
        const bottomSpace = trailing <= MAX_TRAILING_MARGIN ? Math.max(bottomGap, trailing) : bottomGap;

        // Lo que se ve de pantalla desde donde está (o se queda fijo) el panel hasta abajo.
        const visible = window.innerHeight - Math.max(stickyTop, parentRect.top) - bottomSpace;

        // Con la página arriba del todo: el alto que cabe en la primera pantalla. Siempre se le permite.
        const firstScreen = window.innerHeight - parentTopInPage - bottomSpace;

        // Hasta dónde llega el contenido de al lado: el panel puede crecer hasta ahí sin alargar la página.
        const siblingsBottomInPage = Array.from(parent.children)
          .filter((child) => child !== el)
          .reduce((max, child) => Math.max(max, child.getBoundingClientRect().bottom + window.scrollY), 0);
        const contentLimit = Math.max(firstScreen, siblingsBottomInPage - parentTopInPage);

        setHeight(Math.max(minHeight, Math.floor(Math.min(Math.max(visible, firstScreen), contentLimit))));
      });
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(document.body);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [ref, stickyTop, bottomGap, minHeight]);

  return height;
};
